import { resolveUserLocale } from '@/services/locale/user-locale.util';
import { Injectable, inject } from '@angular/core';
import { MAT_DATE_LOCALE, NativeDateAdapter } from '@angular/material/core';
import { TranslocoService } from '@jsverse/transloco';
import { MONTH_KEYS, DAY_KEYS } from './constants';

/**
 * DateAdapter that uses Transloco translations for month/day-of-week names
 * in the calendar popup, while formatting dates in the input field
 * according to the browser's locale (navigator.language).
 */
@Injectable()
export class TranslocoDateAdapter extends NativeDateAdapter {
  private readonly transloco = inject(TranslocoService);

  constructor() {
    super(resolveUserLocale());
  }

  override getMonthNames(style: 'long' | 'short' | 'narrow'): string[] {
    const prefix =
      style === 'long' ? 'components.date-input.monthsLong' : 'components.date-input.months';
    return MONTH_KEYS.map(key => this.transloco.translate(`${prefix}.${key}`));
  }

  override getDayOfWeekNames(style: 'long' | 'short' | 'narrow'): string[] {
    const prefix = style === 'long' ? 'components.date-input.daysLong' : 'components.date-input.days';
    const names = DAY_KEYS.map(key =>
      this.transloco.translate(`${prefix}.${key}`)
    );
    if (style === 'narrow') {
      return names.map(name => name.charAt(0).toUpperCase());
    }
    return names;
  }

  override parse(value: unknown): Date | null {
    if (typeof value === 'string') {
      const raw = value.trim();
      if (!raw) return null;

      // Support common typed formats like 30.07.2026, 30/07/2026, 30-07-2026.
      const dayFirstMatch = raw.match(
        /^(\d{1,2})[./-](\d{1,2})[./-](\d{4})(?:\s+(\d{1,2}):(\d{2}))?$/
      );
      if (dayFirstMatch) {
        const day = Number(dayFirstMatch[1]);
        const month = Number(dayFirstMatch[2]);
        const year = Number(dayFirstMatch[3]);
        const hours = Number(dayFirstMatch[4] ?? 0);
        const minutes = Number(dayFirstMatch[5] ?? 0);

        if (
          month >= 1 &&
          month <= 12 &&
          day >= 1 &&
          day <= 31 &&
          hours >= 0 &&
          hours <= 23 &&
          minutes >= 0 &&
          minutes <= 59
        ) {
          const parsed = new Date(year, month - 1, day, hours, minutes, 0, 0);
          // Guard against JS date rollover (e.g. 31.02.2026).
          if (
            parsed.getFullYear() === year &&
            parsed.getMonth() === month - 1 &&
            parsed.getDate() === day
          ) {
            return parsed;
          }
        }
      }
    }

    return super.parse(value);
  }
}

export const USER_MAT_DATE_LOCALE_PROVIDER = {
  provide: MAT_DATE_LOCALE,
  useFactory: () => resolveUserLocale(),
};
